import * as Popover from '@radix-ui/react-popover';
import { BodyText, UnstyledIconButton } from '@utilitywarehouse/hearth-react';
import { CloseSmallIcon } from '@utilitywarehouse/hearth-react-icons';
import { RefObject, useEffect, useState } from 'react';

import { FeatureFlags } from '@constants/featureFlags';
import { useFeatureFlag } from '@hooks/useFeatureFlag';

import './styles.scss';

interface TutorialPopupProps {
  anchorRef: RefObject<HTMLElement | null>;
  text: string;
  tutorialKey: string;
  showBackdrop?: boolean;
}

interface TutorialPopupUnwrappedProps {
  anchorRef: RefObject<HTMLElement | null>;
  open: boolean;
  text: string;
  showBackdrop?: boolean;
  onBackdropClick?: () => void;
  onClose: () => void;
}

const TutorialPopupUnwrapped = ({
  anchorRef,
  open,
  text,
  showBackdrop = true,
  onClose,
  onBackdropClick,
}: TutorialPopupUnwrappedProps) => {
  if (!anchorRef.current) return null;
  const radixAnchorRef = {
    current: anchorRef.current,
  };
  return (
    <Popover.Root open={open}>
      <Popover.Anchor virtualRef={radixAnchorRef} />
      {open && showBackdrop && (
        <div className="TutorialPopup-backdrop" onClick={onBackdropClick} />
      )}
      <Popover.Portal>
        <Popover.Content
          className="TutorialPopup"
          side="bottom"
          align="start"
          sideOffset={6}
          alignOffset={28}
        >
          <Popover.Arrow
            className="TutorialPopup-arrow"
            width={21}
            height={12}
          />
          <BodyText size="md" color="inverted">
            {text}
          </BodyText>
          <UnstyledIconButton
            label="Close tooltip"
            size="sm"
            onClick={onClose}
            className="TutorialPopup-close"
            inverted
          >
            <CloseSmallIcon />
          </UnstyledIconButton>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

const TutorialPopup = ({
  anchorRef,
  text,
  tutorialKey,
  showBackdrop = true,
}: TutorialPopupProps) => {
  const [open, setOpen] = useState(false);
  const { value: isPartnerPopupEnabled } = useFeatureFlag(
    FeatureFlags.PARTNER_POPUP_ENABLED,
    false
  );

  useEffect(() => {
    const seen = localStorage.getItem(tutorialKey) === 'true';
    setOpen(!seen);
  }, [tutorialKey]);
  const closePopup = () => {
    localStorage.setItem(tutorialKey, 'true');
    setOpen(false);
  };

  if (!isPartnerPopupEnabled) return null;

  return (
    <TutorialPopupUnwrapped
      anchorRef={anchorRef}
      open={open}
      text={text}
      showBackdrop={showBackdrop}
      onClose={closePopup}
      onBackdropClick={closePopup}
    />
  );
};

export type { TutorialPopupUnwrappedProps };
export { TutorialPopup, TutorialPopupUnwrapped };
